import { useState, useEffect, useCallback } from 'react';
import { palette, transition } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import { AddressAutocomplete } from '../../components/common/AddressAutocomplete';
import type { AddressResult } from '../../components/common/AddressAutocomplete';
import { ClientPicker, type ClientSelection } from '../../components/common/ClientPicker';
import { DatePicker } from '../../components/common/DatePicker';
import { TimePicker } from '../../components/common/TimePicker';
import { MultiDatePicker } from '../../components/common/MultiDatePicker';
import { buildRRule } from '../../utils/recurrence';
import { LABEL, INPUT } from './config';

interface TeamUser { id: string; first_name: string; last_name: string; }

type DateMode = 'single' | 'recurring' | 'multi';
const REPEAT_OPTIONS = [
  { value: 'weekly', label: 'Every week' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Every month' },
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Mon–Fri' },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: Record<string, unknown>, userId: string) => Promise<string | null>;
  onCreateRecurring?: (data: Record<string, unknown>, rrule: string, userId: string) => Promise<void>;
  onCreateBatch?: (data: Record<string, unknown>, dates: string[], userId: string) => Promise<void>;
  users: TeamUser[];
  userId: string;
}

export function CreateJobPanel({ isOpen, onClose, onCreate, onCreateRecurring, onCreateBatch, users, userId }: Props) {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  // Rule #33: full ClientSelection captured. work_orders today has NO
  // client_id column — only denormalized client_name + client_phone —
  // so clientId is held in component state but NOT persisted. Adding
  // work_orders.client_id is tracked as a follow-up schema migration
  // (Q5 post-close) to reach parity with bookings.client_id +
  // projects.client_id. Until then, name/phone from selection round-trip.
  const [clientSelection, setClientSelection] = useState<ClientSelection | null>(null);
  const [priority, setPriority] = useState('medium');
  const [assigned, setAssigned] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Date mode
  const [dateMode, setDateMode] = useState<DateMode>('single');
  const [repeatPattern, setRepeatPattern] = useState('weekly');
  const [multiDates, setMultiDates] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset form on open; key-remount refactor out of scope for §1.7
      setTitle(''); setDesc(''); setClientId(null); setClientSelection(null); setPriority('medium');
      setAssigned(''); setDate(''); setTime(''); setLocation(''); setAddress(''); setNotes('');
      setError(null); setDateMode('single'); setRepeatPattern('weekly'); setMultiDates([]);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) { setError('Title is required'); return; }
    setSaving(true); setError(null);

    const baseData = {
      title: title.trim(), description: desc || null,
      // clientId intentionally not persisted — work_orders lacks client_id today.
      client_name: clientSelection?.name ?? null,
      client_phone: clientSelection?.phone ?? null,
      priority, assigned_id: assigned || null,
      assigned_type: assigned ? 'employee' : null,
      scheduled_date: date || null, scheduled_time: time || null,
      location: location || null, address: address || null, notes: notes || null,
    };

    const id = await onCreate(baseData, userId);

    if (id) {
      // Handle recurring/batch schedule entries
      if (dateMode === 'recurring' && date && onCreateRecurring) {
        const rrule = buildRRule(repeatPattern, date);
        await onCreateRecurring({ ...baseData, job_id: id }, rrule, userId);
      } else if (dateMode === 'multi' && multiDates.length > 0 && onCreateBatch) {
        await onCreateBatch({ ...baseData, job_id: id }, multiDates, userId);
      }
      onClose();
    } else {
      setError('Failed to create job');
    }
    setSaving(false);
  }, [title, desc, clientSelection, priority, assigned, date, time, location, address, notes, userId, onCreate, onCreateRecurring, onCreateBatch, onClose, dateMode, repeatPattern, multiDates]);

  const F: React.CSSProperties = { marginBottom: 14 };
  const MODE_BTN = (active: boolean): React.CSSProperties => ({
    padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer',
    fontSize: 11.5, fontWeight: 500, fontFamily: 'inherit',
    background: active ? palette.g800 : 'transparent',
    color: active ? palette.white : palette.g500,
    transition: `all ${transition.fast}`,
  });

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title="New Job" subtitle="Create a work order">
      {error && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{error}</div>}

      <div style={F}><label style={LABEL}>Title *</label><input style={INPUT} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Kitchen faucet repair" /></div>
      <div style={F}><label style={LABEL}>Description</label><textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={desc} onChange={e => setDesc(e.target.value)} /></div>
      <div style={F}><label style={LABEL}>Client</label>
        <ClientPicker
          value={clientId}
          onChange={(sel) => { setClientId(sel?.clientId ?? null); setClientSelection(sel); }}
        />
      </div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Priority</label>
          <select style={INPUT} value={priority} onChange={e => setPriority(e.target.value)}>
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
          </select>
        </div>
        <div style={{ flex: 1 }}><label style={LABEL}>Assign to</label>
          <select style={INPUT} value={assigned} onChange={e => setAssigned(e.target.value)}>
            <option value="">Unassigned</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
          </select>
        </div>
      </div>

      {/* Date mode selector */}
      <div style={{ ...F, marginBottom: 8 }}>
        <label style={LABEL}>Schedule</label>
        <div style={{ display: 'flex', gap: 0, background: palette.g100, borderRadius: 8, padding: 2, width: 'fit-content' }}>
          <button onClick={() => setDateMode('single')} style={MODE_BTN(dateMode === 'single')}>Single date</button>
          <button onClick={() => setDateMode('recurring')} style={MODE_BTN(dateMode === 'recurring')}>Recurring</button>
          <button onClick={() => setDateMode('multi')} style={MODE_BTN(dateMode === 'multi')}>Multiple dates</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}>
          <label style={LABEL}>{dateMode === 'multi' ? 'Dates' : dateMode === 'recurring' ? 'Start date' : 'Scheduled date'}</label>
          {dateMode === 'multi' ? (
            <MultiDatePicker value={multiDates} onChange={setMultiDates} />
          ) : (
            <DatePicker value={date || null} onChange={(v) => setDate(v ?? '')} />
          )}
        </div>
        <div style={{ flex: 1 }}><label style={LABEL}>Time</label>
          <TimePicker value={time || null} onChange={(v) => setTime(v ?? '')} step={15} />
        </div>
      </div>

      {dateMode === 'recurring' && (
        <div style={F}>
          <label style={LABEL}>Repeat</label>
          <select style={INPUT} value={repeatPattern} onChange={e => setRepeatPattern(e.target.value)}>
            {REPEAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      )}

      <div style={F}><label style={LABEL}>Location</label><input style={INPUT} value={location} onChange={e => setLocation(e.target.value)} /></div>
      <div style={F}>
        <label style={LABEL}>Address</label>
        <AddressAutocomplete
          initialValue={address}
          onSelect={(r: AddressResult) => setAddress(r.fullAddress)}
        />
      </div>
      <div style={F}><label style={LABEL}>Notes</label><textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} /></div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={handleSubmit} disabled={saving} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: saving ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{saving ? 'Creating...' : 'Create Job'}</button>
      </div>
    </SlidePanel>
  );
}
